<template>
  <div>
    <!-- Search and Filter Controls -->
    <Stack
      gap="2"
      class="tw-mb-6"
    >
      <div class="tw-flex tw-items-center tw-gap-4">
        <!-- Search -->
        <input
          v-model="searchQuery"
          class="stk-input tw-grow"
          placeholder="Search meetings..."
          icon-left="search"
          @input="filterMeetings"
        >

        <!-- Date Filter -->
        <input
          v-model="dateFilter"
          class="stk-input"
          type="date"
          @input="filterMeetings"
        >

        <!-- Reset Filters -->
        <Button
          :disabled="!canClearFilters"
          variant="primary"
          @click="clearFilters"
        >
          Reset Filters
        </Button>
      </div>

      <div
        v-if="possibleStatuses.length"
        class="statuses"
      >
        <p class="statuses-label">
          Status
        </p>
        <DropdownCheckbox
          v-for="item in possibleStatuses"
          :key="item.statusName"
          v-model="item.checked"
        >
          <MeetingBadge :status-name="item.statusName" />
          <span class="tw-ml-auto">{{ item.name }}</span>
          <span class="tw-ml-auto">{{ item.count }}</span>
        </DropdownCheckbox>
      </div>
    </Stack>

    <Stack v-if="paginatedMeetings.length > 0">
      <!-- Table -->
      <table class="table is-bordered is-fullwidth is-hoverable meetings-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Status</th>
            <template v-if="variant === 'upcoming'">
              <th>Bot</th>
            </template>
            <th>Title</th>
            <th>Contact & Sync Status</th>
            <th>Attendees</th>
            <template v-if="variant === 'upcoming'">
              <th>Meeting Prep</th>
            </template>
            <template v-if="variant === 'past'">
              <th>Actions</th>
            </template>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="meeting in paginatedMeetings"
            :key="meeting.id"
          >
            <!-- Table columns remain the same -->
            <td class="date-cell">
              {{ formatDate(meeting.meeting_date) }}
            </td>

            <td class="status-cell">
              <MeetingBadge
                :meeting="meeting"
                :status-name="meeting.statusName"
              />
            </td>

            <td
              v-if="variant === 'upcoming'"
              class="bot-cell"
            >
              <div
                v-if="meeting.meeting_url"
                class="tw-relative tw-flex tw-items-center tw-justify-center"
              >
                <i
                  class="fas tw-text-3xl"
                  :class="meeting.has_bots || meeting.bots?.length ? 'fa-toggle-on' : 'fa-toggle-off tw-text-gray-400'"
                  @click="toggleBot(meeting)"
                />
                <Loader
                  v-if="togglePendingFor && togglePendingFor === meeting.id"
                  class="tw-absolute tw-right-0"
                />
              </div>
              <span
                v-else
                class="tw-text-xs tw-text-gray-400"
              >No meeting link</span>
            </td>

            <!-- Summary Column -->
            <td class="title-cell">
              <template v-if="meeting.title">
                {{ meeting.title }}
              </template>
              <template v-else-if="meeting.summary">
                {{ meeting.summary.substring(0, 100) }}...
              </template>
            </td>

            <!-- New Contact & Sync Status column -->
            <td class="contact-cell">
              <LoaderWithText
                v-if="meetingPrepLoadingStates[meeting.id]"
                size="3_5"
              >
                Generating…
              </LoaderWithText>

              <Stack
                v-else
                gap="2"
              >
                <template
                  v-if="meeting.crm_connection_id"
                >
                  <LoaderWithText
                    v-if="contactLoadingStates[meeting.crm_connection_id]"
                    size="3_5"
                  />

                  <Buttons
                    v-else-if="crmContacts[meeting.crm_connection_id]"
                    gap="2"
                  >
                    <Badge status="primary">
                      {{ crmContacts[meeting.crm_connection_id].name }}
                    </Badge>
                    <Button
                      v-if="variant === 'upcoming'"
                      size="3"
                      icon-before="fas fa-edit"
                      spacing="tight"
                      @click="showCrmLinkingModalId = meeting.id"
                    >
                      Edit
                    </Button>
                  </Buttons>
                </template>

                <div class="tw-flex tw-gap-2">
                  <Badge
                    v-if="meeting.summary_pushed_to_crm"
                    status="neutral"
                    size="small"
                  >
                    Summary Pushed
                  </Badge>

                  <!-- <Badge
                    v-if="meeting.crm_tasks?.some(task => task.pushed_to_crm)"
                    status="neutral"
                    size="small"
                  >
                    {{ meeting.crm_tasks.filter(task => task.pushed_to_crm).length }} Tasks Pushed
                  </Badge> -->
                </div>
              </Stack>
            </td>

            <td class="attendees-cell">
              <Badges v-if="meeting.attendees.length > 0">
                <Badge
                  v-for="attendee in meeting.attendees"
                  :key="attendee"
                  status="info"
                >
                  {{ attendee }}
                </Badge>
              </Badges>

              <span v-else>
                No attendees
              </span>
            </td>

            <template v-if="variant === 'upcoming'">
              <td class="prep-cell">
                <Button
                  v-if="meeting.meeting_primer"
                  size="3_5"
                  :disabled="meetingPrepLoadingStates[meeting.id]"
                  @click="openMeetingPrep(meeting.id)"
                >
                  View Meeting Prep
                </Button>
                <LoaderWithText
                  v-else-if="meetingPrepLoadingStates[meeting.id]"
                  size="3_5"
                  disabled
                >
                  <span>Fetching meeting primer...</span>
                </LoaderWithText>
                <Button
                  v-else
                  variant="primary"
                  size="3_5"
                  @click="handleGenerateMeetingPrep(meeting)"
                >
                  Generate Meeting Prep
                </Button>
                <StakeModal
                  v-model="isModalVisible[meeting.id]"
                >
                  <CrmLinkingModal
                    v-if="showCrmLinkingModalId === meeting.id"
                    :meeting-id="meeting.id"
                    title="Generate a Meeting Prep"
                    message="Choose which contact to generate the meeting prep for."
                    @contact-linked="handlePullPrepFromCrm(meeting.id)"
                    @close="showCrmLinkingModalId = null"
                  />
                </StakeModal>
              </td>
            </template>

            <template v-if="variant === 'past'">
              <!-- Entity Column -->
              <!-- <td
                v-slot="props"
                field="entity.name"
                label="Person"
              >
                <LinkEntityToMeeting
                  :meeting="meeting"
                  @updated="handleMeetingUpdate"
                >
                  <Button
                    :variant="meeting.entity ? '' : 'primary'"
                    size="3_5"
                    spacing="tight"
                  >
                    {{ meeting.entity?.name || 'Link Entity' }}
                  </Button>
                </LinkEntityToMeeting>
              </td> -->

              <!-- Actions Column -->
              <td class="actions-cell">
                <Buttons
                  size="3_5"
                  class="actions-buttons"
                >
                  <Button
                    v-if="meeting.status === 'completed'"
                    variant="primary"
                    spacing="tight"
                    icon-before="fa fa-eye"
                    :to="meeting.entity?.external_id ?
                      {
                        name: 'entity-note-taking',
                        params: { entityExternalId: meeting.entity?.external_id },
                        query: { meetingId: meeting.id }
                      } :
                      {
                        name: 'meeting',
                        params: { meetingId: meeting.id },
                        query: { meetingId: meeting.id }
                      }"
                  >
                    View
                  </Button>
                  <Button
                    v-else-if="!meeting.s3_key"
                    icon-before="fas fa-upload"
                    variant=""
                    spacing="tight"
                    @click="showUploadModal = true; meetingForUpload = meeting"
                  >
                    Upload
                  </Button>

                  <DeleteMeeting
                    :meeting="meeting"
                    @deleted="handleMeetingUpdate"
                  >
                    <Button
                      icon-before="fas fa-trash"
                      variant="danger"
                      spacing="tight"
                    />
                  </DeleteMeeting>
                </Buttons>
              </td>
            </template>
          </tr>
        </tbody>
      </table>

      <div
        v-if="totalPages > 1"
        class="pagination"
      >
        <p class="pagination-info">
          Page <strong>{{ currentPage }}</strong> of <strong>{{ totalPages }}</strong>
        </p>

        <Buttons>
          <Button
            icon-before="fas fa-arrow-left"
            :disabled="currentPage === 1"
            @click="previousPage"
          >
            Previous
          </Button>
          <Button
            icon-after="fas fa-arrow-right"
            :disabled="currentPage === totalPages"
            @click="nextPage"
          >
            Next Page
          </Button>
        </Buttons>
      </div>
    </Stack>

    <div
      v-else
      class="empty"
    >
      <Callout
        status="primary"
        class="empty-callout"
      >
        <Stack
          class="empty-stack"
          gap="2"
        >
          <template v-if="searchQuery || dateFilter || hiddenStatuses.length > 0">
            <p>No meeting matches these criterias.</p>
            <Button
              :disabled="!canClearFilters"
              variant="primary"
              @click="clearFilters"
            >
              Reset Filters
            </Button>
          </template>
          <template v-else-if="variant === 'upcoming'">
            <Title size="4">
              No Upcoming Meetings
            </Title>
            <p>Connect your calendar to start tracking your meetings</p>
            <Button
              variant="primary"
              size="large"
              href="/ui/advisor/integrations"
            >
              Link Calendar
            </Button>
          </template>
          <template v-else>
            <p>No past meetings found.</p>
          </template>
        </Stack>
      </Callout>
    </div>

    <StakeModal v-model="showMeetingPrimer">
      <Stack gap="4">
        <Title size="5">
          General Summary
        </Title>
        <p>{{ meetingPrimer.summary }}</p>
        <Title size="5">
          Suggested Questions
        </Title>
        <ul class="tw-list-disc tw-list-inside tw-text-gray-600 tw-mb-6">
          <li
            v-for="(item, index) in meetingPrimer.suggested_questions"
            :key="index"
          >
            {{ item }}
          </li>
        </ul>
        <Title size="5">
          Financial Summary
        </Title>
        <table class="table is-striped is-hoverable is-fullwidth is-bordered">
          <tbody>
            <tr
              v-for="(value, key) in meetingPrimer.financial_summary"
              :key="key"
            >
              <td class="tw-font-semibold tw-pr-4">
                {{ key }}
              </td>
              <td>{{ value }}</td>
            </tr>
          </tbody>
        </table>
      </Stack>
    </StakeModal>

    <UploadRecording
      v-model="showUploadModal"
      :meeting="meetingForUpload"
      class="tw-z-[60]"
      @meeting-created="handleMeetingUpdate"
    />
  </div>
</template>

<script>
import DeleteMeeting from '@frontend/ui/features/meetings/components/DeleteMeeting.vue';
import {
  Badge,
  Badges,
  Button,
  Buttons,
  Callout,
  DropdownCheckbox,
  Loader,
  LoaderWithText,
  Stack,
  Title,
} from '@stakeui';
import StakeModal from '@stakeui/StakeModal.vue';
import CrmLinkingModal from '../../example/components/CrmLinkingModal.vue';
import MeetingBadge from './MeetingBadge.vue';
import UploadRecording from '@features/meetings/components/UploadRecording.vue';

import * as Sentry from '@sentry/vue';
import { awaitTask } from '../../checkup/helpers/tasks';
// import { fetchWrapper } from '@frontend/shared/fetch-wrapper.js';

// Allows us to limit concurrent api requests
import pLimit from 'p-limit';
const limit = pLimit(5); // Max open requests at a time

import { debounce } from 'lodash';


export default {
  name: 'MeetingsTable',
  components: {
    DeleteMeeting,
    CrmLinkingModal,
    Badge,
    Badges,
    Button,
    Buttons,
    Callout,
    DropdownCheckbox,
    Loader,
    LoaderWithText,
    Stack,
    Title,
    StakeModal,
    MeetingBadge,
    UploadRecording,
  },
  props: {
    meetings: {
      type: Array,
      default: () => [],
    },
    variant: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      filteredMeetings: [],
      loading: false,
      total: 0,
      perPage: 10,
      currentPage: 1,
      sortField: 'meeting_date',
      sortOrder: this.variant === 'upcoming' ? 'asc' : 'desc',
      searchQuery: '',
      dateFilter: '',
      meetingPrimer: '',
      showCrmLinkingModalId: null,
      showMeetingPrimer: false,
      togglePendingFor: null,
      meetingPrepLoadingStates: {},
      possibleStatuses: [],
      crmContacts: {},
      contactLoadingStates: {},
      showUploadModal: false,
      meetingForUpload: null,
      debouncedFetchContacts: null,
    };
  },
  computed: {
    headers() {
      return {
        'X-CSRFToken': this.$root.$data.csrfToken,
        'Content-Type': 'application/json',
      };
    },
    totalPages() {
      return Math.ceil(this.filteredMeetings.length / this.perPage);
    },
    paginatedMeetings() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredMeetings.slice(start, start + this.perPage);
    },
    isModalVisible() {
      return {
        [this.showCrmLinkingModalId]: true,
      };
    },
    transformedMeetings() {
      return this.meetings.map(meeting => {
        let statusName = 'No Recording';

        if (meeting.status) {
          if (meeting.status === 'scheduled') {
            statusName = 'Bot Scheduled';
          } else if (['ignored', 'upcoming'].includes(meeting.status)) {
            const date = new Date(meeting.meeting_date);
            statusName = date > new Date() ? 'Upcoming' : 'Ignored';
          } else {
            statusName = this.formatStatus(meeting.status);
          }
        }

        return {
          ...meeting,
          statusName,
        };
      });
    },
    hiddenStatuses() {
      return [...new Set(
          this.possibleStatuses
              .filter(status => !status.checked)
              .map(status => status.statusName),
      )];
    },
    canClearFilters() {
      return this.searchQuery !== '' ||
        this.dateFilter !== '' ||
        this.hiddenStatuses.length > 0;
    },
  },
  watch: {
    transformedMeetings: {
      immediate: true,
      handler() {
        this.setStatusFilter();
        this.filterMeetings();
      },
    },
    paginatedMeetings: {
      // immediate: true,
      handler(newMeetings) {
        this.debouncedFetchContacts(newMeetings);
      },
    },
    hiddenStatuses() {
      this.filterMeetings();
    },
  },
  created() {
    this.debouncedFetchContacts = debounce(this.fetchContactsOnPage, 500);
  },
  methods: {
    formatStatus(status) {
      // Assumes snake case
      const words = status.split('_');

      // Capitalize first letter of each word
      const formatted = words.map(word => {
        return word.charAt(0).toUpperCase() + word.slice(1);
      });

      return formatted.join(' ');
    },
    setStatusFilter() {
      this.possibleStatuses = this.transformedMeetings.reduce((acc, { statusName }) => {
        const existing = acc.find(item => item.statusName === statusName);

        if (existing) {
          existing.count++;
        } else {
          if (this.variant === 'upcoming') {
            acc.push({ statusName, count: 1, checked: true });
          } else {
            acc.push({ statusName, count: 1, checked: statusName === 'Completed' });
          }
        }

        return acc;
      }, []);
    },
    formatDate(date) {
      if (!date) return 'N/A';
      const options = {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      };
      return new Date(date).toLocaleString('en-US', options);
    },
    formatDateForComparison(date) {
      return date ? new Date(date).toISOString().split('T')[0] : '';
    },
    async fetchMeeting(meetingId) {
      const meetingResponse = await fetch(`/api/v1/meetings/${meetingId}/`);
      const meetingData = await meetingResponse.json();
      // Update corresponding meeting in this.meetings array
      const meetingIndex = this.meetings.findIndex(m => m.id === meetingId);
      if (meetingIndex !== -1) {
        // Ignoring this eslint error. Not ideal but seems fragile, trying to fix might cause new bugs.
        // eslint-disable-next-line vue/no-mutating-props
        this.meetings[meetingIndex] = meetingData;
        // Also update in filteredMeetings array
        const filteredIndex = this.filteredMeetings.findIndex(m => m.id === meetingId);
        if (filteredIndex !== -1) {
          this.filteredMeetings[filteredIndex] = meetingData;
        }
      }
      return meetingData;
    },
    async handlePullPrepFromCrm(meetingId) {
      this.$set(this.meetingPrepLoadingStates, meetingId, true);
      try {
        const meeting = await this.fetchMeeting(meetingId);
        if (!meeting.crm_connection_id) {
          this.showCrmLinkingModalId = meetingId;
          return;
        }
        console.log('MEETING PULL PREP FROM CRM', meeting);
        await this.generatePrimer(meeting);
        alert('Successfully pulled prep from CRM');
      } catch (error) {
        console.error('ERROR', error);
        Sentry.captureException(error);
        this.$oruga.notification.open({
          message: 'Failed to pull prep from CRM',
          variant: 'danger',
        });
      } finally {
        this.$set(this.meetingPrepLoadingStates, meetingId, false);
      }
    },
    async generatePrimer(meeting) {
      if (meeting.meeting_primer) {
        if (!window.confirm('Are you sure you want to re-pull the data from the CRM? This will remove any edits you would have made.')) {
          return;
        }
      }

      try {
        console.log('GENERATING PRIMER');
        let meetingPrimerUrl = '';
        if (meeting.entity?.external_id) {
          meetingPrimerUrl = `/api/v1/meeting-primer/create/${meeting.entity.external_id}/`;
        } else {
          meetingPrimerUrl = `/api/v1/meeting-primer/create-via-meeting/${meeting.id}/`;
        }

        const response = await awaitTask(meetingPrimerUrl, {
          method: 'POST',
          headers: this.headers,
        });

        if (response.task.status === 'complete') {
          // Fetch and update the meeting data
          const updatedMeeting = await this.fetchMeeting(meeting.id);

          // Update the meetings array directly
          const meetingIndex = this.meetings.findIndex(m => m.id === meeting.id);
          if (meetingIndex !== -1) {
            this.$set(this.meetings, meetingIndex, updatedMeeting);
          }

          // Update filtered meetings as well
          const filteredIndex = this.filteredMeetings.findIndex(m => m.id === meeting.id);
          if (filteredIndex !== -1) {
            this.$set(this.filteredMeetings, filteredIndex, updatedMeeting);
          }

          this.openMeetingPrep(meeting.id);
        } else {
          throw new Error('Failed to generate primer');
        }
      } catch (error) {
        console.error('ERROR', error);
        Sentry.captureException(error);
        this.$oruga.notification.open({
          message: 'Failed to generate primer',
          variant: 'danger',
        });
      } finally {
      }
    },
    filterMeetings() {
      let filtered = [...this.transformedMeetings];

      // Apply search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(meeting =>
          meeting.title?.toLowerCase().includes(query) ||
          meeting.summary?.toLowerCase().includes(query) ||
          meeting.source?.toLowerCase().includes(query) ||
          meeting.attendees?.some(attendee => attendee.toLowerCase().includes(query)),
        );
      }

      // Apply date filter
      if (this.dateFilter) {
        filtered = filtered.filter(meeting => {
          const meetingDate = this.formatDateForComparison(meeting.meeting_date);
          return meetingDate === this.dateFilter;
        });
      }

      // Using hiddenStatuses instead of activeStatuses
      // so that we can clear that filter simply
      // by emptying the array
      if (this.hiddenStatuses) {
        filtered = filtered.filter(meeting => {
          return !this.hiddenStatuses.includes(meeting.statusName);
        });
      }

      // Sort by meeting_date or created_date
      filtered.sort((a, b) => {
        const dateA = a.meeting_date || a.created_date;
        const dateB = b.meeting_date || b.created_date;
        // For upcoming meetings: earliest first (ascending)
        // For past meetings: most recent first (descending)
        return this.variant === 'upcoming' ?
          new Date(dateA) - new Date(dateB) :
          new Date(dateB) - new Date(dateA);
      });

      this.filteredMeetings = filtered;
      this.total = filtered.length;
      this.currentPage = 1; // Reset to first page when filtering
    },
    clearFilters() {
      this.searchQuery = '';
      this.dateFilter = '';
      this.possibleStatuses = this.possibleStatuses.map(item => ({ ...item, checked: true }));
      this.filterMeetings();
    },
    nextPage() {
      if (this.currentPage < this.totalPages) this.currentPage++;
    },
    previousPage() {
      if (this.currentPage > 1) this.currentPage--;
    },
    viewMeeting(meeting) {
      this.$emit('view-meeting', meeting);
    },
    handleMeetingUpdate(updatedMeeting=null) {
      this.$emit('updated');
    },
    openMeetingPrep(id) {
      const meeting = this.meetings.find(item => item.id === id);

      if (meeting) {
        this.meetingPrimer = meeting.meeting_primer;
        this.showMeetingPrimer = true;
      }
    },
    handleGenerateMeetingPrep(meeting) {
      // We already have a synced contact, so we just generate the primer
      if (meeting.crm_connection_id) {
        this.handlePullPrepFromCrm(meeting.id);
        return;
      }

      // We show the modal so that the user can choose a contact first
      this.showCrmLinkingModalId = meeting.id;
    },
    async toggleBot(meeting) {
      const errorNotification = {
        message: 'Unable to toggle meeting bot. Please try again or contact support.',
        variant: 'danger',
        duration: 5000,
      };

      try {
        this.togglePendingFor = meeting.id;

        const res = await fetch(`/api/v1/recall/meetings/${meeting.id}/bot/`, {
          method: 'POST',
          headers: this.headers,
        });
        const data = await res.json();

        if (!res.ok) {
          // Here we don't need to throw
          // Backend should have already triggered a Sentry
          this.$oruga.notification.open(errorNotification);
          this.togglePendingFor = null;
          return;
        }

        if (data.meeting?.id) {
          const i = this.filteredMeetings.findIndex(m => m.id === data.meeting.id);

          if (i === -1) {
            throw new Error(`Meeting ${data.meeting.id} not found in filteredMeetings`);
          } else {
          // Update meeting while maintaining reactivity
            this.$set(this.filteredMeetings, i, data.meeting);
          }
        } else {
          throw new Error('Response from bot toggle request did not include updated meeting');
        }
      } catch (error) {
        // Handle unexpected errors
        this.$oruga.notification.open(errorNotification);

        // Rethrow for Sentry
        throw error;
      } finally {
        this.togglePendingFor = null;
      }
    },
    async fetchContactsOnPage(meetingsOnPage) {
      limit.clearQueue();
      const contactsOnPage = this.getContactsOnPage(meetingsOnPage);
      const uniqueContacts = this.getUniqueContacts(contactsOnPage);

      const requests = uniqueContacts.map(contact => {
        // Limits number of concurrent requests
        // If we are at the limit, waits for one to finish before starting a new one.
        return limit(() => this.fetchCrmContact(contact.crm, contact.id));
      });
      await Promise.all(requests);
    },
    getContactsOnPage(meetingsOnPage) {
      return meetingsOnPage.map(m => ({
        crm: m.crm_type, id: m.crm_connection_id,
        //
      })).filter(c => {
        return c.crm && c.id && !this.crmContacts[c.id];
      });
    },
    // Dedupes contacts to avoid fetching the same one multiple times
    getUniqueContacts(contacts) {
      const keys = contacts.map(c => `${c.crm}|${c.id}`);

      return Array.from(new Set(keys)).map(key => {
        const [crm, id] = key.split('|');
        return { crm, id };
      });
    },
    async fetchCrmContact(selectedCrm, crmConnectionId) {
      if (crmConnectionId && !this.contactLoadingStates[crmConnectionId]) {
        this.$set(this.contactLoadingStates, crmConnectionId, true);
        try {
          const response = await fetch(`/api/v1/crm/${selectedCrm.toLowerCase()}/contact/${crmConnectionId}/`, {
            headers: this.headers,
          });
          let data = { name: `${selectedCrm} Contact` };
          if (response.ok) data = await response.json();
          this.$set(this.crmContacts, crmConnectionId, data);
        } catch (error) {
          console.error('Error fetching CRM contact:', error);
          Sentry.captureException(error);
        } finally {
          this.$set(this.contactLoadingStates, crmConnectionId, false);
        }
      }
    },
  },
};
</script>

<style scoped>
.meetings-table {
  margin-bottom: 0 !important;

  td {
    vertical-align: middle;
  }
}

.attendees-cell {
  width: 16em;
}

.status-cell {
  width: 8em;
}

.bot-cell {
  width: 7em;
  white-space: nowrap;
}

.date-cell {
  width: 13em;
  white-space: nowrap;
}

.prep-cell {
  width: 14em;
  white-space: nowrap;
}

.actions-cell {
  width: 10em;
  white-space: nowrap;
}

.actions-buttons {
  flex-wrap: nowrap;
}

.empty {
  display: flex;
  justify-content: center;
  text-align: center;
  align-items: center;
}

.empty-callout {
  justify-content: center;
  max-width: 24em;
  padding: 2em 3em;
}

.contact-cell {
  width: 14em;
}

.statuses {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.statuses-label {
  line-height: 2rem;
}
</style>
